<!-- src/views/CreateRecipe.vue -->
<template>
  <div class="main bg-primary">
    <TopDeck
      v-if="authStore.userInfos"
      :firstElement="pageTitle"
      secondElement="une recette de base"
      :coloredSecond="true"
      actionHeaderIcon="mdi-arrow-left"
      :actionheaderFunction="() => $router.push('/browse_base_recipes')"
      actionHeaderColor="accent"
      actionHeaderTextColor="white"
    />

    <v-form ref="form" v-model="isValid" lazy-validation class="mt-5">
      <!-- Métadonnées recette -->
      <v-card outlined class="mb-6 rounded-xl pa-2 px-2" color="secondary">
        <v-card-title class="text-h6">Informations recette</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field v-model="recipe.title" label="Nom de la recette" variant="outlined" required :hide-details="true"/>
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model.number="recipe.servings" type="number" min="1" label="Portions" variant="outlined" :hide-details="true" />
            </v-col>
            <v-col cols="12">
              <label class="text-body-2 mb-2 d-block">Catégorie(s)</label>
              <div class="d-flex flex-wrap" style="gap:8px;">
                <button
                  v-for="cat in recipeCategories"
                  :key="cat.value"
                  type="button"
                  class="recipe-tag-btn"
                  :class="{ 'recipe-tag-btn--active': recipe.categories.includes(cat.value) }"
                  @click="recipe.categories.includes(cat.value) ? recipe.categories.splice(recipe.categories.indexOf(cat.value), 1) : recipe.categories.push(cat.value)"
                >{{ cat.text }}</button>
              </div>
            </v-col>
            <v-col cols="12">
              <label class="text-body-2 mb-2 d-block">Tags (optionnel)</label>
              <div class="d-flex flex-wrap" style="gap:8px;">
                <button
                  v-for="tag in RECIPE_TAGS"
                  :key="tag"
                  type="button"
                  class="recipe-tag-btn"
                  :class="{ 'recipe-tag-btn--active': recipe.tags.includes(tag) }"
                  @click="recipe.tags.includes(tag) ? recipe.tags.splice(recipe.tags.indexOf(tag), 1) : recipe.tags.push(tag)"
                >{{ tag }}</button>
              </div>
            </v-col>
            <v-col cols="12">
              <label class="text-body-2 ml-3 mb-1 d-block">Notes (optionnel)</label>
              <RichTextEditor v-model="recipe.notes" placeholder="Ajoutez vos notes ici (listes, mise en forme...)" />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="recipe.videoUrl"
                label="Lien vidéo (YouTube, Instagram, TikTok — optionnel)"
                variant="outlined"
                :hide-details="true"
                clearable
                prepend-inner-icon="mdi-video-outline"
              />
            </v-col>
            <v-col cols="12">
              <v-row style="gap:1rem; align-items:center;">
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="recipe.imageUrl"
                    label="Image URL (optionnel)"
                    variant="outlined"
                    :hide-details="true"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center justify-end">
                  <v-sheet v-if="recipe.imageUrl" class="pa-0 preview-sheet" elevation="0" style="max-width:140px">
                    <v-img
                      :src="previewSrc"
                      width="140"
                      height="96"
                      contain
                      aspect-ratio="16/11"
                      @error="onPreviewError"
                    >
                      <template #placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                          <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
                        </v-row>
                      </template>
                    </v-img>
                  </v-sheet>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-row>
        <!-- 1) Sélection d'ingrédients -->
        <v-col cols="12" md="7">
          <v-card color="secondary" class="rounded-xl pa-2 px-2" outlined>
            <v-card-title class="justify-space-between mb-2">
              <span class="text-h6">Ajouter des ingrédients</span>
              <v-btn size="small" color="accent" variant="tonal" class="rounded-lg" @click="newIngredientDialog.open = true">
                <v-icon class="mr-1">mdi-plus</v-icon>
                Nouvel ingrédient
              </v-btn>
            </v-card-title>

            <v-card-text>
              <v-text-field
                v-model="searchQuery"
                placeholder="Rechercher par nom, marque ou catégorie…"
                prepend-inner-icon="mdi-magnify"
                variant="outlined"
                density="comfortable"
                clearable
              />

              <v-skeleton-loader v-if="isLoadingIngredients" type="list-item-two-line@6" class="mt-2" />
              <div v-else>
                <v-alert v-if="!ingredients.length" type="info" variant="tonal" class="mb-2">
                  Aucun ingrédient dans votre collection pour l'instant.
                </v-alert>

                <v-alert v-else-if="!filteredIngredients.length" type="warning" variant="tonal" class="mb-2">
                  Aucun résultat pour « {{ searchQuery }} ».
                </v-alert>

                <v-virtual-scroll :items="filteredIngredients" height="360">
                  <template #default="{ item }">
                    <v-list-item
                      :title="item.productName"
                      :subtitle="subtitleFor(item)"
                      class="px-2"
                    >
                      <template #append>
                        <v-btn size="small" variant="tonal" class="mr-2" @click="openAddDialog(item)">
                          Ajouter
                        </v-btn>
                      </template>
                    </v-list-item>
                    <v-divider />
                  </template>
                </v-virtual-scroll>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- 2) Ingrédients de la recette -->
        <v-col cols="12" md="5">
          <v-card color="secondary" class="rounded-xl pa-2 px-2">
            <v-card-title class="text-h6">Ingrédients sélectionnés</v-card-title>
            <v-card-text>
              <v-alert v-if="!recipeIngredients.length" type="info" variant="tonal" class="mb-4">
                Aucun ingrédient ajouté pour l'instant.
              </v-alert>

              <div v-else class="mb-4">
                <v-row
                  v-for="(line, idx) in recipeIngredients"
                  :key="line.id"
                  align="center"
                  class="py-1"
                >
                  <v-col cols="7">
                    <div class="font-medium">{{ line.productName }}</div>
                    <div class="text-caption text-medium-emphasis">{{ line.productBrand || '—' }}</div>
                  </v-col>
                  <v-col cols="5" class="d-flex align-center justify-end">
                    <v-text-field
                      v-model.number="line.grams"
                      type="number"
                      min="0"
                      step="1"
                      suffix="g"
                      hide-details
                      density="compact"
                      style="max-width: 120px"
                      @change="clampGrams(line)"
                    />
                    <v-btn icon variant="text" color="error" @click="removeLine(idx)" class="ml-2">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-col>

                  <v-col cols="12" class="py-0">
                    <div class="text-caption">
                      ≈ {{ kcalFor(line).toFixed(0) }} kcal — Prot {{ protFor(line).toFixed(1) }} g • Gras {{ carbsFor(line).toFixed(1) }} g • Lip {{ fatFor(line).toFixed(1) }} g
                    </div>
                  </v-col>

                  <v-divider class="my-2" />
                </v-row>
              </div>

              <!-- Totaux -->
              <v-sheet class="pa-3 rounded-lg" color="tertiary">
                <div class="text-subtitle-2 mb-1">Totaux recette</div>
                <div class="text-body-2">
                  {{ totalGrams }} g — {{ totals.kcal.toFixed(0) }} kcal
                  <br>
                  P {{ totals.protein.toFixed(1) }} g • G {{ totals.carbs.toFixed(1) }} g • L {{ totals.fat.toFixed(1) }} g
                </div>
                <div v-if="recipe.servings > 0" class="text-caption mt-2">
                  <strong>Par portion :</strong>
                  {{ (totals.kcal / recipe.servings).toFixed(0) }} kcal —
                  Prot {{ (totals.protein / recipe.servings).toFixed(1) }} g •
                  Gras {{ (totals.carbs / recipe.servings).toFixed(1) }} g •
                  Lip {{ (totals.fat / recipe.servings).toFixed(1) }} g
                </div>
              </v-sheet>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Actions -->
      <div class="d-flex justify-end mt-6">
        <v-btn class="mr-2 rounded-lg" variant="outlined" @click="resetRecipe">Réinitialiser</v-btn>
        <v-btn color="accent" :disabled="!canSave" @click="saveRecipe" class="rounded-lg">
          Enregistrer la recette
        </v-btn>
      </div>

      <!-- Dialog nouvel ingrédient -->
      <v-dialog v-model="newIngredientDialog.open" max-width="680" scrollable>
        <v-card>
          <v-card-title class="text-h6 pa-4 pb-2">Nouvel ingrédient</v-card-title>
          <v-card-text class="pt-2">

            <!-- Infos générales -->
            <div class="dialog-section-label">Informations générales</div>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="newIngredientDialog.name" label="Nom *" variant="outlined" hide-details clearable />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="newIngredientDialog.brand" label="Marque" variant="outlined" hide-details clearable />
              </v-col>
              <v-col cols="12" md="6">
                <v-combobox v-model="newIngredientDialog.category" :items="ingredientCategories" label="Catégorie" variant="outlined" hide-details clearable />
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field v-model.number="newIngredientDialog.poids" label="Poids (g)" type="number" min="0" variant="outlined" hide-details />
              </v-col>
            </v-row>

            <!-- Image & lien -->
            <div class="dialog-section-label mt-4">Image & lien produit</div>
            <v-row align="center">
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="newIngredientDialog.productImage"
                  label="URL de l'image"
                  variant="outlined"
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-image-outline"
                />
              </v-col>
              <v-col cols="12" md="4" class="d-flex justify-center">
                <v-avatar v-if="newIngredientDialog.productImage" size="64" rounded="lg" style="border: 1.5px solid rgba(117,83,252,0.3);">
                  <v-img :src="newIngredientDialog.productImage" cover />
                </v-avatar>
                <div v-else class="ing-img-placeholder rounded-lg d-flex align-center justify-center">
                  <v-icon size="28" color="grey-lighten-1">mdi-image-off-outline</v-icon>
                </div>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="newIngredientDialog.productLink"
                  label="Lien vers la page produit (URL)"
                  variant="outlined"
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-link"
                />
              </v-col>
            </v-row>

            <!-- Scores -->
            <div class="dialog-section-label mt-4">Scores nutritionnels</div>
            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  v-model="newIngredientDialog.nutriscore"
                  :items="NUTRISCORE_OPTIONS"
                  label="Nutriscore"
                  variant="outlined"
                  hide-details
                  clearable
                >
                  <template #selection="{ item }">
                    <v-chip :color="nutriscoreColor(item.value)" size="small" label class="font-weight-bold text-white">
                      {{ item.value || '—' }}
                    </v-chip>
                  </template>
                  <template #item="{ item, props: p }">
                    <v-list-item v-bind="p">
                      <template #prepend>
                        <v-chip :color="nutriscoreColor(item.value)" size="small" label class="font-weight-bold text-white mr-2">
                          {{ item.value }}
                        </v-chip>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="newIngredientDialog.novaValue"
                  :items="NOVA_OPTIONS"
                  label="Score NOVA"
                  variant="outlined"
                  hide-details
                  clearable
                >
                  <template #selection="{ item }">
                    <v-chip :color="novaColor(item.value)" size="small" label class="font-weight-bold text-white">
                      Nova {{ item.value }}
                    </v-chip>
                  </template>
                  <template #item="{ item, props: p }">
                    <v-list-item v-bind="p">
                      <template #prepend>
                        <v-chip :color="novaColor(item.value)" size="small" label class="font-weight-bold text-white mr-2">
                          Nova {{ item.value }}
                        </v-chip>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <!-- Valeurs nutritionnelles -->
            <div class="dialog-section-label mt-4">Valeurs nutritionnelles (pour 100 g)</div>
            <v-row>
              <v-col cols="6" md="3">
                <v-text-field v-model.number="newIngredientDialog.kcal" label="Kcal *" type="number" min="0" variant="outlined" hide-details />
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field v-model.number="newIngredientDialog.lipides" label="Lipides (g) *" type="number" min="0" variant="outlined" hide-details />
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field v-model.number="newIngredientDialog.agSatures" label="AG saturés (g)" type="number" min="0" variant="outlined" hide-details />
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field v-model.number="newIngredientDialog.glucides" label="Glucides (g) *" type="number" min="0" variant="outlined" hide-details />
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field v-model.number="newIngredientDialog.sucre" label="Sucres (g)" type="number" min="0" variant="outlined" hide-details />
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field v-model.number="newIngredientDialog.proteines" label="Protéines (g) *" type="number" min="0" variant="outlined" hide-details />
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field v-model.number="newIngredientDialog.fibres" label="Fibres (g)" type="number" min="0" variant="outlined" hide-details />
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field v-model.number="newIngredientDialog.sel" label="Sel (g)" type="number" min="0" variant="outlined" hide-details />
              </v-col>
            </v-row>

            <!-- Magasins -->
            <div class="dialog-section-label mt-4">Disponible dans ces magasins</div>
            <div class="store-grid mt-2 mb-1">
              <v-chip
                v-for="store in AVAILABLE_STORES"
                :key="store"
                :color="newIngredientDialog.availableStores.includes(store) ? 'accent' : undefined"
                :variant="newIngredientDialog.availableStores.includes(store) ? 'flat' : 'tonal'"
                :class="newIngredientDialog.availableStores.includes(store) ? 'text-white font-weight-bold' : ''"
                size="small"
                label
                class="store-chip"
                @click="toggleDialogStore(store)"
              >
                <v-icon start size="14">{{ newIngredientDialog.availableStores.includes(store) ? 'mdi-check' : 'mdi-store-outline' }}</v-icon>
                {{ store }}
              </v-chip>
            </div>

            <!-- Caché -->
            <v-checkbox
              v-model="newIngredientDialog.hidden"
              label="Ingrédient caché (lié aux recettes de base, non visible dans la bibliothèque générale)"
              color="accent"
              hide-details
              class="mt-3"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="newIngredientDialog.open = false">Annuler</v-btn>
            <v-btn color="accent" :disabled="!canSaveNewIngredient" :loading="newIngredientDialog.saving" @click="saveNewIngredient">Enregistrer</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog ajout avec grammes -->
      <v-dialog v-model="addDialog.open" max-width="480">
        <v-card>
          <v-card-title>Ajouter « {{ addDialog.item?.productName }} »</v-card-title>
          <v-card-text>
            <v-text-field
              v-model.number="addDialog.grams"
              type="number"
              min="0"
              label="Quantité (g)"
              variant="outlined"
            />
            <div class="text-caption mt-1">
              Réf. 100 g : {{ nutritionLine(addDialog.item) }}
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn variant="text" @click="addDialog.open=false">Annuler</v-btn>
            <v-btn color="accent" @click="confirmAdd">Ajouter</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Messages -->
      <v-snackbar v-model="snack.show" :timeout="2500">
        {{ snack.text }}
      </v-snackbar>
      <v-alert v-if="errorMessage" type="error" variant="tonal" class="mt-4">
        {{ errorMessage }}
      </v-alert>
    </v-form>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import { useAuthStore } from '@/store/auth_store'
  import { getBaseIngredients, AVAILABLE_STORES } from '@/service/base_ingredient_service'
  import { getHiddenBaseIngredients } from '@/service/hidden_base_ingredient_service'
  import { doc, setDoc } from 'firebase/firestore'
  import { db } from '@/firebase'
  import md5 from 'md5'
  import { addRecipeToBaseRecipes, getBaseRecipeById, updateBaseRecipe } from '@/service/base_recipe_service'
  import TopDeck from "@/components/Utils/TopDeck.vue";
  import RichTextEditor from '@/components/Utils/RichTextEditor.vue'


  const authStore = useAuthStore()

  /** ------- Constantes scores ------- **/
  const NUTRISCORE_OPTIONS = ['A', 'B', 'C', 'D', 'E']
  const NOVA_OPTIONS = [1, 2, 3, 4]

  function nutriscoreColor(v) {
    const map = { A: 'green', B: 'light-green', C: 'yellow-darken-3', D: 'orange', E: 'red' }
    return map[v] || 'grey'
  }
  function novaColor(v) {
    const map = { 1: 'green', 2: 'light-green', 3: 'orange', 4: 'red' }
    return map[v] || 'grey'
  }
  function buildNovaGroup(novaValue) {
    if (!novaValue) return { color: 'grey', string: '' }
    return { color: novaColor(novaValue), string: `nova.${novaValue}` }
  }

  /** ------- Données UI ------- **/
  const recipeCategories = [
    { text: 'Entrée', value: 'Starter' },
    { text: 'Plat principal', value: 'Meal' },
    { text: 'Dessert', value: 'Dessert' },
    { text: 'Collation', value: 'Snacks' },
    { text: 'Petit-déjeuner', value: 'Breakfast' },
  ]

  const editId = ref(null) // id de recette si mode édition
  const isEdit = computed(() => !!editId.value)
  const route = useRoute()
  const pageTitle = computed(() => isEdit.value ? 'Modifier' : 'Créer')

  const RECIPE_TAGS = [
    'Jus', 'Soupes', 'Cakes', 'Smoothies', 'Desserts',
    'Chaud', 'Froid', 'Méditerranéen', 'Salades', 'Sauces', 'Pains'
  ]

  const recipe = ref({
    title: '',
    servings: 1,
    categories: [],
    tags: [],
    notes: '',
    imageUrl: ''
  })

  const ingredients = ref([])
  const isLoadingIngredients = ref(false)
  const errorMessage = ref('')

  const searchQuery = ref('')
  const recipeIngredients = ref([])

  const snack = ref({ show: false, text: '' })
  const isValid = ref(true)

  // Preview handling for recipe image: track whether the provided URL loads
  const validPreview = ref(true)
  const placeholderImage = 'https://www.mimisrecipes.com/wp-content/uploads/2018/12/recipe-placeholder-featured.jpg'

  const previewSrc = computed(() => {
    return validPreview.value && recipe.value.imageUrl ? recipe.value.imageUrl : placeholderImage
  })

  function onPreviewError() {
    validPreview.value = false
  }

  // Reset preview validity when imageUrl changes
  watch(() => recipe.value.imageUrl, (v) => {
    validPreview.value = !!v
  })

  /** ---------- Helpers Firestore → JS ---------- **/
  function unwrapFirestoreValue(v) {
    if (v == null) return null
    if (v.stringValue !== undefined) return v.stringValue
    if (v.doubleValue !== undefined) return v.doubleValue
    if (v.integerValue !== undefined) return Number(v.integerValue)
    if (v.booleanValue !== undefined) return v.booleanValue
    if (v.arrayValue !== undefined) {
      const arr = v.arrayValue.values || []
      return arr.map(unwrapFirestoreValue)
    }
    if (v.mapValue !== undefined) {
      const out = {}
      const f = v.mapValue.fields || {}
      for (const k of Object.keys(f)) out[k] = unwrapFirestoreValue(f[k])
      return out
    }
    return v
  }

  function parseNumberFromString(s) {
    const m = String(s ?? '').match(/-?\d+(\.\d+)?/)
    return m ? Number(m[0]) : 0
  }

  function parseNutrimentsMap(nutrimentsMap) {
    // nutrimentsMap attendu: [{ key:'energy-kcal', value:'264 kcal' }, ...]
    const out = { kcal:0, protein:0, carbs:0, fat:0, sugars:0, salt:0, fiber:0, satFat:0 }
    if (!Array.isArray(nutrimentsMap)) return out
    for (const it of nutrimentsMap) {
      const key = it?.key
      const val = parseNumberFromString(it?.value)
      switch (key) {
        case 'energy-kcal': out.kcal = val; break
        case 'proteins': out.protein = val; break
        case 'carbohydrates': out.carbs = val; break
        case 'fat': out.fat = val; break
        case 'sugars': out.sugars = val; break
        case 'salt': out.salt = val; break
        case 'fiber': out.fiber = val; break
        case 'saturated-fat': out.satFat = val; break
      }
    }
    return out
  }

  function normalizeFirestoreIngredients(raw) {
    // Cas 1: le service renvoie déjà un objet simple { barcode: { ... } }
    if (raw && !raw.fields) {
      return Object.entries(raw).map(([id, val]) => {
        const nutritionPer100g =
          val.nutritionPer100g ||
          parseNutrimentsMap(val.nutriments_map) ||
          {}
        return {
          id,
          productName: val.productName || '',
          productBrand: val.productBrand || '',
          category: val.category || '',
          favorite: !!val.favorite,
          productImage: val.productImage || '',
          productLink: val.productLink || '',
          nutriscore: val.nutriscore || '',
          availableStores: Array.isArray(val.availableStores) ? val.availableStores : [],
          servingSize: val.servingSize || '',
          servingQuantity: val.servingQuantity ?? '',
          allergens: Array.isArray(val.allergens) ? val.allergens : [],
          labels: Array.isArray(val.labels) ? val.labels : [],
          novaGroup: val.novaGroup || null,
          nutriments_map: val.nutriments_map || [],
          nutritionPer100g,
        }
      })
    }

    // Cas 2: payload Firestore proto (document.fields.*)
    const fields = raw?.fields
    if (!fields) return []
    const asObject = unwrapFirestoreValue({ mapValue: { fields } }) // => { barcode: { ...js } }
    return Object.entries(asObject).map(([id, val]) => {
      const nutritionPer100g =
        parseNutrimentsMap(val.nutriments_map) || {}
      return {
        id,
        productName: val.productName || '',
        productBrand: val.productBrand || '',
        category: val.category || '',
        favorite: !!val.favorite,
        productImage: val.productImage || '',
        productLink: val.productLink || '',
        nutriscore: val.nutriscore || '',
        availableStores: Array.isArray(val.availableStores) ? val.availableStores : [],
        servingSize: val.servingSize || '',
        servingQuantity: val.servingQuantity ?? '',
        allergens: Array.isArray(val.allergens) ? val.allergens : [],
        labels: Array.isArray(val.labels) ? val.labels : [],
        novaGroup: val.novaGroup || null,
        nutriments_map: val.nutriments_map || [],
        nutritionPer100g,
      }
    })
  }

  // ------- Chargement ingrédients user + édition -------
  onMounted(() => {
    // récupère l'id si présent en query: /add_recipe?id=xxx
    editId.value = typeof route.query.id === 'string' ? route.query.id : null

    watch(
      () => authStore.userInfos,
      async () => {
        if (!authStore?.userid) return
        try {
          isLoadingIngredients.value = true
          // Ingrédients de base (disponibles pour tous)
          const baseData = await getBaseIngredients()
          const baseIngredients = normalizeFirestoreIngredients(baseData).map(it => ({
            ...it,
            source: 'base'
          }))

          // Ingrédients de base cachés
          const hiddenData = await getHiddenBaseIngredients()
          const hiddenBaseIngredients = normalizeFirestoreIngredients(hiddenData).map(it => ({
            ...it,
            source: 'hidden_base'
          }))

          ingredients.value = [...baseIngredients, ...hiddenBaseIngredients];
          // après avoir les ingrédients, si on est en mode édition -> hydrate
          await tryHydrateEdit()
        } catch (e) {
          console.error('Erreur récupération ingrédients:', e)
          errorMessage.value = "Erreur lors du chargement des ingrédients."
        } finally {
          isLoadingIngredients.value = false
        }
      },
      { immediate: true }
    )

    // si l'id dans l'URL change en live (rare), on tente l'hydratation
    watch(() => route.query.id, (val) => {
      editId.value = typeof val === 'string' ? val : null
      hydratedOnce = false
      tryHydrateEdit()
    })
  })

  /** ------- Recherche ------- **/
  const filteredIngredients = computed(() => {
    const q = (searchQuery.value || '').trim().toLowerCase();
    if (!q) return ingredients.value;
    return ingredients.value.filter((item) => {
      return ['productName', 'productBrand', 'category'].some((key) => {
        return (item[key] ?? '').toLowerCase().includes(q);
      });
    });
  });

  /** ------- Ajout à la recette ------- **/
  const addDialog = ref({ open: false, item: null, grams: 100 })

  const ingredientCategories = [
    "Fruits", "Légumes", "Légumineuses", "Céréales", "Viandes",
    "Poissons", "Produits laitiers", "Œufs", "Matières grasses",
    "Épices", "Herbes", "Autres"
  ]

  const emptyIngredientForm = () => ({
    open: false, saving: false,
    name: "", brand: "", category: "",
    poids: null, kcal: null, lipides: null, glucides: null,
    proteines: null, fibres: null, sel: null, sucre: null,
    agSatures: null,
    productImage: "", productLink: "",
    nutriscore: "", novaValue: null,
    availableStores: [],
    hidden: true,
  })

  const newIngredientDialog = ref(emptyIngredientForm())

  const canSaveNewIngredient = computed(() =>
    newIngredientDialog.value.name?.trim() &&
    newIngredientDialog.value.kcal !== null &&
    newIngredientDialog.value.lipides !== null &&
    newIngredientDialog.value.glucides !== null &&
    newIngredientDialog.value.proteines !== null
  )

  const generateIngredientId = (name, brand, category) => {
    const raw = `${name}|${brand}|${category}`
    const hash = md5(raw)
    const sanitize = (s) =>
      String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .toLowerCase().trim().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "")
    const prefix = sanitize(name) || hash.slice(0, 8)
    return `${prefix}_${hash.slice(0, 8)}`
  }

  function toggleDialogStore(store) {
    const arr = newIngredientDialog.value.availableStores
    const idx = arr.indexOf(store)
    if (idx === -1) arr.push(store)
    else arr.splice(idx, 1)
  }

  async function saveNewIngredient() {
    if (!canSaveNewIngredient.value) return
    newIngredientDialog.value.saving = true
    try {
      const f = newIngredientDialog.value
      const id = generateIngredientId(f.name, f.brand, f.category)
      const nutriments_map = [
        { key: "energy-kcal", value: f.kcal != null ? `${f.kcal} kcal` : "" },
        { key: "fat", value: f.lipides != null ? `${f.lipides} g` : "" },
        { key: "saturated-fat", value: f.agSatures != null ? `${f.agSatures} g` : "" },
        { key: "carbohydrates", value: f.glucides != null ? `${f.glucides} g` : "" },
        { key: "proteins", value: f.proteines != null ? `${f.proteines} g` : "" },
        { key: "fiber", value: f.fibres != null ? `${f.fibres} g` : "" },
        { key: "salt", value: f.sel != null ? `${f.sel} g` : "" },
        { key: "sugar", value: f.sucre != null ? `${f.sucre} g` : "" },
      ].filter(n => n.value)

      const ingredient = {
        productName: f.name.trim(),
        productBrand: f.brand || "",
        category: f.category || "",
        servingQuantity: f.poids || null,
        servingSize: f.poids ? `${f.poids} g` : null,
        productImage: f.productImage.trim(),
        productLink: f.productLink.trim(),
        nutriscore: f.nutriscore || "",
        novaGroup: buildNovaGroup(f.novaValue),
        availableStores: [...f.availableStores],
        added_dt: new Date().toISOString(),
        isEditingDisabled: true,
        isBaseIngredient: true,
        hidden: f.hidden,
        nutriments_map,
      }

      const collection = f.hidden ? "HiddenBaseIngredients" : "BaseIngredients"
      const docRef = doc(db, collection, "all")
      await setDoc(docRef, { [id]: ingredient }, { merge: true })

      // Ajoute à la liste locale pour apparition immédiate
      const newItem = {
        id,
        productName: ingredient.productName,
        productBrand: ingredient.productBrand,
        category: ingredient.category,
        source: f.hidden ? 'hidden_base' : 'base',
        ...ingredient,
        nutritionPer100g: parseNutrimentsMap(nutriments_map),
      }
      ingredients.value = [...ingredients.value, newItem]

      snack.value = { show: true, text: `Ingrédient "${ingredient.productName}" ajouté ✅` }
      newIngredientDialog.value = emptyIngredientForm()
    } catch (err) {
      console.error("Erreur saveNewIngredient:", err)
      snack.value = { show: true, text: "Erreur lors de l'enregistrement" }
    } finally {
      newIngredientDialog.value.saving = false
    }
  }

  function openAddDialog(item) {
    addDialog.value = { open: true, item, grams: 100 }
  }

  function extractPer100g(item) {
    // priorité : nutritionPer100g normalisé, sinon parse nutriments_map
    if (item?.nutritionPer100g) return item.nutritionPer100g
    return parseNutrimentsMap(item?.nutriments_map || [])
  }

  // --------- Pré-remplissage en mode édition ----------
  let hydratedOnce = false

  async function tryHydrateEdit() {
    if (!isEdit.value || hydratedOnce) return
    if (!authStore.userid) return
    // on a besoin que la collection d'ingrédients soit dispo pour hydrater joliment
    if (!ingredients.value.length) return

    try {
      const r = await getBaseRecipeById(authStore.userid, editId.value)
      if (!r) {
        errorMessage.value = 'Recette introuvable.'
        return
      }

      // Pré-remplir meta
      recipe.value = {
        title: r.title || '',
        servings: Number(r.servings || 1),
        categories: Array.isArray(r.categories) ? r.categories : (r.category ? [r.category] : []),
        tags: Array.isArray(r.tags) ? r.tags : [],
        notes: r.notes || '',
        imageUrl: r.imageUrl || r.url || '',
        videoUrl: r.videoUrl || ''
      }

      // Hydrater lignes ingréds [{id, grams}] -> [{id, grams, productName, brand, nutritionPer100g}]
      const lines = Array.isArray(r.ingredients) ? r.ingredients : []
      recipeIngredients.value = lines.map((l) => {
        const found = ingredients.value.find(it => it.id === l.id)
        return {
          id: l.id,
          grams: Number(l.grams || 0),
          productName: found?.productName || l.id,
          productBrand: found?.productBrand || '',
          nutritionPer100g: extractPer100g(found) // {} si pas trouvé
        }
      })

      hydratedOnce = true
    } catch (e) {
      console.error('❌ Hydratation édition échouée:', e)
      errorMessage.value = "Impossible de pré-remplir la recette."
    }
  }

  function confirmAdd() {
    const { item, grams } = addDialog.value
    if (!item || grams <= 0) return
    const existing = recipeIngredients.value.find(l => l.id === item.id)
    if (existing) {
      existing.grams += Number(grams)
    } else {
      recipeIngredients.value.push({
        id: item.id,
        name: item.productName,
        productName: item.productName,
        productBrand: item.productBrand,
        grams: Number(grams),
        nutritionPer100g: extractPer100g(item)
      })
    }
    addDialog.value.open = false
  }

  function removeLine(index) { recipeIngredients.value.splice(index, 1) }
  function clampGrams(line) {
    if (line.grams == null || isNaN(line.grams) || line.grams < 0) line.grams = 0
  }

  /** ------- Nutrition & totaux ------- **/
  function factor(line) { return (line.grams || 0) / 100 }
  function kcalFor(line)  { return (line.nutritionPer100g.kcal   || 0) * factor(line) }
  function protFor(line)  { return (line.nutritionPer100g.protein|| 0) * factor(line) }
  function carbsFor(line) { return (line.nutritionPer100g.carbs  || 0) * factor(line) }
  function fatFor(line)   { return (line.nutritionPer100g.fat    || 0) * factor(line) }

  const totals = computed(() => {
    return recipeIngredients.value.reduce((acc, l) => {
      const f = factor(l)
      acc.kcal   += (l.nutritionPer100g.kcal    || 0) * f
      acc.protein+= (l.nutritionPer100g.protein || 0) * f
      acc.carbs  += (l.nutritionPer100g.carbs   || 0) * f
      acc.fat    += (l.nutritionPer100g.fat     || 0) * f
      acc.sugars += (l.nutritionPer100g.sugars  || 0) * f
      acc.salt   += (l.nutritionPer100g.salt    || 0) * f
      return acc
    }, { kcal:0, protein:0, carbs:0, fat:0, sugars:0, salt:0 })
  })

  const totalGrams = computed(() =>
    recipeIngredients.value.reduce((s, l) => s + (l.grams || 0), 0)
  )

  /** ------- Helpers UI ------- **/
  function subtitleFor(item) {
    const left = [item.productBrand, item.category].filter(Boolean).join(' • ')
    const n = extractPer100g(item)
    const right = `${n.kcal || 0} kcal / 100 g`
    return left ? `${left}  —  ${right}` : right
  }
  
  function nutritionLine(item) {
    const n = extractPer100g(item)
    return `${n.kcal || 0} kcal • P ${n.protein || 0} g • G ${n.carbs || 0} g • L ${n.fat || 0} g / 100 g`
  }

  /** ------- Validation & Enregistrement ------- **/
  const canSave = computed(() => {
    return !!recipe.value.title && recipeIngredients.value.length > 0 && isValid.value
  })

  async function saveRecipe() {
    if (!authStore.userid) {
      errorMessage.value = "Utilisateur non connecté."
      return
    }
    const payload = {
      date: Date.now(),
      title: recipe.value.title,
      imageUrl: recipe.value.imageUrl || '',
      videoUrl: recipe.value.videoUrl || '',
      categories: recipe.value.categories || [],
      category: recipe.value.categories[0] || null,
      tags: recipe.value.tags || [],
      servings: Number(recipe.value.servings || 1),
      notes: formatNotesAsHtml(recipe.value.notes),
      ingredients: recipeIngredients.value.map(l => ({ id: l.id, name: l.name || l.productName, grams: l.grams })),
      totals: {
        grams: totalGrams.value,
        kcal: Number(totals.value.kcal.toFixed(0)),
        protein: Number(totals.value.protein.toFixed(1)),
        carbs: Number(totals.value.carbs.toFixed(1)),
        fat: Number(totals.value.fat.toFixed(1)),
        sugars: Number(totals.value.sugars.toFixed(1)),
        salt: Number(totals.value.salt.toFixed(2))
      }
    }

    try {
      if (isEdit.value) {
        // UPDATE
        await updateBaseRecipe(authStore.userid, editId.value, {
          ...payload,
          baseRecipe: true,
          date: Date.now(),
        })  
        snack.value = { show: true, text: 'Recette mise à jour ✅' }
      } else {
        // CREATE
        await addRecipeToBaseRecipes(authStore.userid, {
          ...payload,
          baseRecipe: true,
          createdAt: Date.now(),
        })
        snack.value = { show: true, text: 'Recette enregistrée ✅' }
        resetRecipe()
      }
    } catch (e) {
      console.error('Erreur enregistrement recette:', e)
      errorMessage.value = isEdit.value
        ? "Erreur lors de la mise à jour de la recette."
        : "Erreur lors de l'enregistrement de la recette."
    }
  }

  function resetRecipe() {
    recipe.value = { title: '', servings: 1, categories: [], tags: [], notes: '', imageUrl: '', videoUrl: '' }
    searchQuery.value = ''
    recipeIngredients.value = []
  }

  function formatNotesAsHtml(text) {
    if (!text) return ''
    return String(text)
      .split('\n')
      .map(line => {
        const trimmed = line.trim()
        if (trimmed.startsWith('-') || trimmed.startsWith('•')) {
          return '<li>' + trimmed.substring(1).trim() + '</li>'
        }
        return trimmed ? '<p>' + trimmed + '</p>' : '<br/>'
      })
      .join('')
      .replace(/(<li>.*?<\/li>)/s, (match) => '<ul>' + match + '</ul>')
  }
</script>

<style scoped>
  .preview-sheet {
    background: transparent;
    border-radius: 6px;
    overflow: hidden;
  }
  .recipe-tag-btn {
    display: inline-flex;
    align-items: center;
    padding: 5px 14px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 400;
    cursor: pointer;
    border: 0.5px solid rgba(0,0,0,0.18);
    color: rgba(0,0,0,0.55);
    background: transparent;
    font-family: inherit;
    transition: all 0.15s;
  }
  .recipe-tag-btn:hover {
    background: rgba(0,0,0,0.05);
  }
  .recipe-tag-btn--active {
    background: #1A1615;
    color: #fff;
    border-color: #1A1615;
  }
  .dialog-section-label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: rgba(var(--v-theme-text), 0.5);
    margin-bottom: 10px;
  }
  .store-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .store-chip {
    cursor: pointer;
    transition: all 0.15s ease;
    user-select: none;
  }
  .store-chip:hover {
    opacity: 0.82;
  }
  .ing-img-placeholder {
    width: 64px;
    height: 64px;
    background: rgba(var(--v-theme-text), 0.06);
    border: 1px dashed rgba(var(--v-theme-text), 0.2);
  }
</style>
